import './Toast.scss';

import { useMemo } from 'react';
import { Button, Stack, Toast as RBToast } from 'react-bootstrap';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faRotate } from '@fortawesome/free-solid-svg-icons';
import { faCircleCheck, faSquareExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import IconButton from '@components/Button/IconButton';
import { ToastItem, ToastType } from '@customTypes/toast';

import { BLOCKED_BY_BROWSER_CONTENT, DEFAULT_ERROR_CONTENT } from './consts';

interface IToastProps extends ToastItem {
  onClose: () => void;
}

const renderErrorString = (errorString?: string) => {
  if (!errorString) return null;

  return <p className='mb-0 fw-bold fs-body small'>{errorString}</p>;
};

const renderCustomActions = (onClose: () => void) => {
  const onReload = () => {
    window.location.reload();
  };

  return (
    <Stack direction='horizontal' gap={2}>
      <IconButton startIcon={faRotate} size='sm' variant='primary' onClick={onReload}>
        Reload
      </IconButton>
      <Button size='sm' variant='secondary' onClick={onClose}>
        Close
      </Button>
    </Stack>
  );
};

const iconColorMap = {
  success: {
    icon: faCircleCheck,
    color: 'green',
  },
  warning: {
    icon: faSquareExclamation,
    color: 'orange',
  },
  danger: {
    icon: faSquareExclamation,
    color: 'red',
  },
  info: {
    icon: faSquareExclamation,
    color: 'blue',
  },
} as const;

export default function Toast({
  type = ToastType.BASIC,
  headerTitle = 'Server Issue',
  content,
  errorString,
  variant = 'success',
  onClose,
  ...otherProps
}: IToastProps) {
  const errorContent = useMemo(() => {
    if (content) return content;

    if (type === ToastType.ACTION_BLOCKED) return BLOCKED_BY_BROWSER_CONTENT;

    return DEFAULT_ERROR_CONTENT;
  }, [content, type]);

  if (type === ToastType.DEFAULT) {
    const isWarning = variant === 'warning';
    return (
      <RBToast
        show
        onClose={onClose}
        bg={variant}
        className='text-start cp-toast pp-toast-default'
        delay={3000}
        autohide
        data-testid='toastContainer'
        onExited={onClose} // Ensures toast is removed after hiding
        {...otherProps}
      >
        <RBToast.Header closeVariant={isWarning ? '#000' : 'white'}>
          <p className={`fs-6 mb-0 ${isWarning ? 'text-black' : 'text-white'} w-100`}>{headerTitle}</p>
        </RBToast.Header>
      </RBToast>
    );
  }

  const { icon, color } = iconColorMap[variant || 'danger'] || iconColorMap.danger;

  return (
    <RBToast show onClose={onClose} className='text-start cp-toast' data-testid='toastContainer' {...otherProps}>
      {type !== ToastType.CUSTOM_ACTIONS && (
        <RBToast.Header data-testid='toastHeader'>
          <FontAwesomeIcon icon={icon as IconDefinition} fontSize={21} color={color} />
          <p className='ms-2 mb-0 fw-semibold me-auto fs-6 lh-sm'>{headerTitle}</p>
        </RBToast.Header>
      )}
      <RBToast.Body className='d-flex flex-column align-items-start gap-2 cp-toast-body'>
        <p className='fs-6 mb-0'>{errorContent}</p>
        {type === ToastType.CUSTOM_ACTIONS && renderErrorString(errorString)}
        {errorString && type !== ToastType.ACTION_BLOCKED && <hr className='my-0 align-self-stretch' />}
        {type !== ToastType.CUSTOM_ACTIONS && renderErrorString(errorString)}
        {type === ToastType.CUSTOM_ACTIONS && renderCustomActions(onClose)}
      </RBToast.Body>
    </RBToast>
  );
}
